import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { addWeeks, format, formatDistanceToNowStrict, getUnixTime, isPast, parseISO } from 'date-fns';
import { memoize } from 'lodash';
import { firstValueFrom } from 'rxjs';
import { debounceTime, filter, switchMap } from 'rxjs/operators';

import { CoreApiService } from '../../../core-api.service';
import { GenerateApiKeyDialog } from './generate-api-key-dialog/generate-api-key-dialog.component';
import { LoggingService } from '../../../logging.service';
import { NotificationService } from '../../../notification.service';
import { ApiKey } from '../../../shared/models/api-key.model';
import { Device } from '../../../shared/models/device.model';
import { TranslationBundle, TranslationService } from '../../../translation.service';
import { ConfirmDialog, DialogService, PortalDialog } from '../../../dialogs';

const jsonParse = memoize(JSON.parse);

interface ApiKeyRow extends ApiKey {
  createdLabel: string
  expiresLabel: string
  expiredTooltip: string
  expireWarning: boolean
  lastUsedLabel: string
  lastUsedDate: Date
}

@Component({
  host: { 'test-id': 'fw-api-key-table' },
  selector: 'fw-api-key-table',
  templateUrl: './fw-api-key-table.component.html',
  styleUrls: ['./fw-api-key-table.component.scss'],
})

export class FwApiKeyTable implements OnChanges {
  dataSource = new MatTableDataSource<ApiKeyRow>();
  displayedColumns: string[] = ['label', 'created', 'expires', 'last_used', 'actions'];
  deviceDisabled = false;
  legacyApiKey = '';
  legacyApiKeyLoginCommand = '';
  loading = false;
  messages = TranslationBundle.EMPTY;
  nameFilter = new FormControl('');
  showKey = false;
  showTerminalCommand = false;

  @Input() apiKeys: ApiKey[] = [];
  @Input() context: 'device' | 'profile' = 'profile';
  @Input() device?: Device;
  @Input() legacyKey?: ApiKey;
  @Output() afterSave = new EventEmitter<void>();

  @ViewChild(MatTable) table: MatTable<string[]>;
  @ViewChild(MatSort) matSort: MatSort;
  @ViewChild('legacyDeletionDialog', { static: true }) legacyDeletionDialog: TemplateRef<unknown>

  constructor(
    private dialog: DialogService,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private translate: TranslationService,
  ) {
    this.nameFilter.valueChanges.pipe(debounceTime(200)).subscribe(name => {
      this.dataSource.filter = JSON.stringify({ label: name.toLowerCase() });
    });
    this.dataSource.filterPredicate = (apiKey, filters) => {
      const { label } = jsonParse(filters);
      if (!apiKey.label.toLowerCase().includes(label)){
        return false;
      }
      return true;
    };
  }

  ngAfterViewInit(): void {
    this.setSort();
  }

  setSort(): void {
    this.dataSource.sort = this.matSort;
    this.dataSource.sort.direction = 'desc';
    this.dataSource.sort.active = 'last_used';
    this.dataSource.sortingDataAccessor = (view, property) => {
      return property === 'last_used' ? view.lastUsedDate : view[property];
    };
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes.apiKeys?.firstChange || changes.apiKeys?.currentValue !== changes.apiKeys?.previousValue) {
      const allKeys = [...this.apiKeys];
      if (this.messages === TranslationBundle.EMPTY) {
        this.messages = await firstValueFrom(this.translate.getBundle('fwApiKeyTable'));
      }
      if (this.legacyKey) {
        allKeys.unshift({
          ...this.legacyKey,
          label: this.messages.get('legacyKey'),
        });
        this.legacyApiKey = `${window.location.host}:${this.legacyKey.key}`;
        this.legacyApiKeyLoginCommand = `fw login ${this.legacyApiKey}`;
      }
      this.loading = false;
      this.dataSource.data = allKeys.map(this.makeApiKeyRow, this);
      this.setSort();
    }

    if (changes.device?.currentValue) {
      this.deviceDisabled = this.device.disabled;
    }
  }

  makeApiKeyRow(key): ApiKeyRow {
    const expireDate = key.expires_at ? parseISO(key.expires_at) : null;
    const isExpired = isPast(expireDate);
    const expiresSoon = expireDate ? (isExpired || (getUnixTime(expireDate) - getUnixTime(addWeeks(new Date(), 1)) < 0)) : false;
    return {
      ...key,
      createdLabel: format(parseISO(key.created), 'MMM dd, yyy'),
      expiresLabel: !expireDate
        ? this.messages.get('na')
        : isExpired
          ? this.messages.get('expired')
          : this.translate.interpolate(this.messages.get('expirationLabel'), formatDistanceToNowStrict(expireDate)),
      expiredTooltip: !expireDate
        ? ''
        : isExpired
          ? this.translate.interpolate(this.messages.get('expiredTooltip'), expireDate.toDateString())
          : this.translate.interpolate(this.messages.get('expiresTooltip'), expireDate.toDateString()),
      expireWarning: expiresSoon,
      lastUsedLabel:
        key.key === this.legacyKey?.key ? this.messages.get('na')
        : key.last_used ? `${formatDistanceToNowStrict(parseISO(key.last_used))} ago`
        : this.messages.get('unused'),
      lastUsedDate: key.last_used ? parseISO(key.last_used) : null,
    }
  }

  generateApiKeyModal(): void {
    const data = this.context === 'profile' ? { context: this.context } : { context: this.context, deviceId: this.device._id }

    this.dialog.open(GenerateApiKeyDialog, { data: data })
      .afterClosed().subscribe((generatedKey: ApiKey) => {
        if (generatedKey) {
          const original = this.dataSource.data.find(key => key.key === generatedKey.key);
          this.afterSave.emit();
          if (!original) {
            this.dataSource.data = [...this.dataSource.data, this.makeApiKeyRow(generatedKey)];
          } else {
            this.dataSource.data = this.dataSource.data.map(key => key.key === generatedKey.key ? this.makeApiKeyRow(generatedKey) : key);
          }
        }
      });
  }

  delete(key): void {
    this.showKey = false;
    this.showTerminalCommand = false;

    if (key.key === this.legacyKey?.key) {
      this.dialog.open(PortalDialog, {
        data: {
          title: this.messages.get('headers.legacyDelete'),
          template: this.legacyDeletionDialog,
        },
        width: '600px',
      }).afterClosed().pipe(
        filter(proceed => Boolean(proceed)),
        switchMap(() => this.context === 'profile' ? this.coreApi.users.apiKeys.delete(key.key) : this.coreApi.devices.deleteKey(this.device._id, key.key))
      ).subscribe({
        next: () => {
          this.notifications.success('common.delete.success');
          this.removeKeyFromTable(key);
          this.afterSave.emit();
        },
        error: (error) => {
          this.notifications.error('common.delete.error');
          this.logger.error(error);
        },
      });
    } else {
      this.dialog.open(ConfirmDialog, {
        data: {
          content: this.translate.interpolate(this.messages.get('delete.content'), key.label),
          title: this.messages.get('delete.title'),
        },
        width: '600px',
      }).afterClosed().pipe(
        filter(proceed => Boolean(proceed)),
        switchMap(() => this.context === 'profile' ? this.coreApi.users.apiKeys.delete(key.key) : this.coreApi.devices.deleteKey(this.device._id, key.key))
      ).subscribe({
        next: () => {
          this.notifications.success('common.delete.success');
          this.removeKeyFromTable(key);
          this.afterSave.emit();
        },
        error: (error) => {
          this.notifications.error('common.delete.error');
          this.logger.error(error);
        },
      });
    }
  }

  removeKeyFromTable(keyToRemove): void {
    this.dataSource.data = this.dataSource.data.filter(key => key.key !== keyToRemove.key);
  }
}
